import React, { useRef, useEffect, memo } from "react";

/**
 * Renders a clickable point in a 3D scene.
 *
 * @param {string} uuid - The unique identifier of the point.
 * @param {Object} vpoint - The position of the point in 3D space.
 * @param {string} color - The color of the point.
 * @param {number} size - The size of the point.
 * @param {Function} callback - The callback function to be called when the point is clicked.
 * @returns {JSX.Element} The rendered point component.
 */
export const PointClick = memo(({ uuid, vpoint, color, size, callback }) => {
  const ref = useRef();

  useEffect(() => {
    if(ref.current && vpoint){
      ref.current.position.set(vpoint.x, vpoint.y, vpoint.z);
    }
  }, [vpoint]);

  const onClick = (e) => {
    if(callback) callback(uuid);
  };

  return (
    <mesh renderOrder={1} ref={ref} onClick={onClick}>
      <sphereGeometry attach="geometry" args={[size, 16, 16]}/>
      <meshBasicMaterial attach="material" color={color} depthTest={true} depthWrite={true} />
    </mesh>
  );
});

/**
 * Renders a collection of clickable points.
 *
 * @param {Array} vpoints - The array of points to render.
 * @param {string} color - The color of the points.
 * @param {number} size - The size of the points.
 * @param {Function} callback - The callback function to be called when a point is clicked.
 * @returns {React.Element|null} - The rendered collection of points or null if vpoints is empty.
 */
export const PointsClick = memo(({ vpoints, color, size, callback }) => {
  if (!vpoints) return null;

  var objects = [];
  vpoints.forEach((v, i) => {
    objects.push(
      <PointClick key={i} uuid={i} vpoint={v} color={color} size={size} callback={callback}/>
    );
  });

  return objects.length !== 0 ? (
    <React.Fragment>{objects}</React.Fragment>
  ) : null;
});
